import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';

/**
 * 한시적 프로그램 다운로드 (사용자 지시 2026-10-02 · V397).
 *
 * <p>환경설정 › 메뉴 관리에서 설치 파일과 기간을 등록하면, 그 기간 동안 상단바의 「관리자 대시보드」
 * 옆에 「{이름} 다운로드」 버튼이 선다. 받는 쪽({@link useActiveProgramDownload})은 로그인만 보고,
 * 올리는 쪽은 메뉴 관리 권한을 본다 — 앱 받기(`AppInstallButton`)와 같은 갈래다.
 *
 * <h2>바이트가 백엔드를 지나가지 않는다</h2>
 * 올리기는 presigned PUT 으로 브라우저가 저장소에 직접 보내고, 받기는 서명된 주소로 브라우저가
 * 직접 간다. 설치 파일은 백엔드의 multipart 상한(40MB)을 넘기 쉽다.
 */

export type ActiveProgramDownload = {
  /** 오늘 기간 안인 줄이 없으면 false. 버튼을 그리지 않는다. */
  available: boolean;
  name: string | null;
  fileName: string | null;
  fileBytes: number | null;
  endsOn: string | null;
};

export const activeProgramDownloadKey = ['program-download-active'] as const;

export function useActiveProgramDownload(enabled = true) {
  return useQuery({
    queryKey: activeProgramDownloadKey,
    queryFn: () => apiFetch<ActiveProgramDownload>('/v1/program-downloads/active'),
    // 등록은 어쩌다 한 번이다. 화면을 옮길 때마다 다시 물을 이유가 없다.
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

/** 상단바 버튼이 부른다. 서명된 주소를 그때 받아 브라우저를 보낸다. */
export async function downloadActiveProgram(): Promise<void> {
  const url = await apiFetch<string>('/v1/program-downloads/active/url');
  window.location.assign(url);
}

export type ProgramDownloadRow = {
  id: string;
  name: string;
  fileName: string;
  fileBytes: number | null;
  /** YYYY-MM-DD · 포함. */
  startsOn: string;
  /** YYYY-MM-DD · 포함. */
  endsOn: string;
  createdAt: string;
  /** 오늘 상단바에 서 있는 줄인가(서버 판정). */
  active: boolean;
};

export type ProgramDownloadStatus = {
  /** 저장소가 설정된 환경인가. 아니면 올리기를 잠근다. */
  storageEnabled: boolean;
  items: ProgramDownloadRow[];
};

export const programDownloadListKey = ['settings-program-downloads'] as const;

export function useProgramDownloads() {
  return useQuery({
    queryKey: programDownloadListKey,
    queryFn: () => apiFetch<ProgramDownloadStatus>('/v1/settings/program-downloads'),
  });
}

export type ProgramDownloadInput = {
  name: string;
  startsOn: string;
  endsOn: string;
  file: File;
};

/**
 * 파일 하나를 저장소에 올리고 등록한다 — ① 올릴 자리를 받고 ② 그 주소로 파일을 보낸 뒤 ③ 등록한다.
 * ③이 거절되면 올라간 파일을 걷는다(`appReleaseApi.useUploadAppRelease` 와 같은 길).
 */
export function useUploadProgramDownload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ProgramDownloadInput) => {
      const { file, ...meta } = input;

      const ticket = await apiFetch<{ objectKey: string; url: string }>(
        `/v1/settings/program-downloads/upload-url?fileName=${encodeURIComponent(file.name)}`,
        { method: 'POST' },
      );

      // 저장소로 직행한다. apiFetch 를 쓰지 않는 이유는 세션 쿠키·CSRF 헤더가 붙으면 서명 검증이 깨지기 때문이다.
      const put = await fetch(ticket.url, { method: 'PUT', body: file });
      if (!put.ok) {
        throw new Error('파일을 저장소에 올리지 못했습니다.');
      }

      try {
        return await apiFetch<ProgramDownloadRow>('/v1/settings/program-downloads', {
          method: 'POST',
          body: { ...meta, objectKey: ticket.objectKey },
        });
      } catch (cause) {
        // 바이트는 이미 올라갔다. 걷지 않으면 어느 줄도 가리키지 않는 파일이 남는다. 걷기가 실패해도 원래 오류를 덮지 않는다.
        await apiFetch<void>(
          `/v1/settings/program-downloads/orphan?objectKey=${encodeURIComponent(ticket.objectKey)}`,
          { method: 'DELETE' },
        ).catch(() => undefined);
        throw cause;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: programDownloadListKey });
      // 상단바 버튼은 5분 캐시라 밀어 주지 않으면 방금 등록한 것이 바로 안 선다.
      queryClient.invalidateQueries({ queryKey: activeProgramDownloadKey });
    },
  });
}

export function useDeleteProgramDownload() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/v1/settings/program-downloads/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: programDownloadListKey });
      queryClient.invalidateQueries({ queryKey: activeProgramDownloadKey });
    },
  });
}

/** 관리 창의 한 줄을 받는다. 기간 밖인 줄도 여기서는 받을 수 있다. */
export async function downloadProgramRow(id: string): Promise<void> {
  const url = await apiFetch<string>(`/v1/settings/program-downloads/${id}/url`);
  window.location.assign(url);
}

/** 고르는 창을 좁힐 뿐이다. 서버가 확장자를 다시 검사한다. */
export const PROGRAM_FILE_ACCEPT = '.exe,.msi,.zip';

export function programSizeText(bytes: number | null): string {
  if (!bytes) return '';
  return `${Math.round(bytes / 104857.6) / 10} MB`;
}
